import { useState, useEffect } from "react";
import { Spinner } from "flowbite-react";
import { MixpanelHeader } from "./MixpanelHeader";
import { MixpanelForm } from "./MixpanelForm";
import { MixpanelCredentialsForm } from "./MixpanelCredentialsForm";
import { useIntegrations } from "../../../../contexts/IntegrationsContext";
import { useAuth } from "../../../../hooks";

export const MixpanelIntegration = () => {
  const { loading, isIntegrationConnected, getIntegrationById } =
    useIntegrations();
  const { apiClient } = useAuth();
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);

  // Credentials state for pre-connection
  const [credentials, setCredentials] = useState({
    project_id: "",
    service_account_username: "",
    service_account_password: "",
  });

  const isConnected = isIntegrationConnected("mixpanel");
  const integration = getIntegrationById("mixpanel");

  // Fetch Mixpanel configuration when connected
  useEffect(() => {
    const fetchConfig = async () => {
      if (!integration?.id) return;

      setConfigLoading(true);
      try {
        const response = await apiClient.get(
          `integration/team-integrations/${integration.id}/`,
        );
        setConfig(response.data.config || {});
      } catch (err) {
        console.error("Failed to fetch Mixpanel config:", err);
      } finally {
        setConfigLoading(false);
      }
    };

    if (isConnected && integration?.id) {
      fetchConfig();
    }
  }, [apiClient, isConnected, integration?.id]);

  // Clear credentials after successful connection
  useEffect(() => {
    if (isConnected) {
      setCredentials({
        project_id: "",
        service_account_username: "",
        service_account_password: "",
      });
    }
  }, [isConnected]);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner size="lg" aria-label="Loading Mixpanel integration" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col gap-4">
      <div className="animation-slide-up-lil-bounce mt-1 flex w-full flex-col gap-4 overflow-x-auto rounded-lg bg-white py-4 shadow">
        <MixpanelHeader
          isConnected={isConnected}
          integration={integration}
          credentials={credentials}
        />

        {!isConnected && (
          <>
            <hr className="border-gray-200" />
            <MixpanelCredentialsForm
              credentials={credentials}
              onCredentialsChange={setCredentials}
            />
          </>
        )}

        {isConnected && (
          <>
            <hr className="border-gray-200" />
            {configLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : (
              <MixpanelForm
                integration={integration}
                config={config}
                onConfigUpdate={setConfig}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
